"use client";

import { useEffect, useState } from "react";
import { useSession } from "next-auth/react";
import { X } from "lucide-react";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import LoginCard from "@/components/auth/LoginCard";
import SignupEntryCard from "@/components/auth/SignupEntryCard";
import EmailSignUpCard from "@/components/auth/EmailSignUpCard";
import {
	AUTH_DIALOG_BODY_CLASSNAME,
	AUTH_DIALOG_CLOSE_CLASSNAME,
	AUTH_DIALOG_CLOSE_POSITION_CLASSNAME,
	AUTH_DIALOG_CONTENT_CLASSNAME,
	AUTH_DIALOG_DEFAULT_RETURN_URL,
	AUTH_DIALOG_TAB_LIST_CLASSNAME,
	authDialogTabTriggerClassName,
} from "@/components/auth/auth-dialog.tokens";

export type AuthDialogProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/**
	 * Where to send the user after successful login when the dialog is used in modal mode.
	 * @default AUTH_DIALOG_DEFAULT_RETURN_URL
	 */
	returnUrl?: string;
	/**
	 * Accessible dialog name. Shown only to screen readers unless `showTitle` is true.
	 * @default "Log in"
	 */
	title?: string;
	/** When true, the title is visible above the form(s). */
	showTitle?: boolean;
	/**
	 * When true, shows a visible Log in / Sign up tab strip above the cards.
	 * When false, only one card is shown at a time; footer links on each card switch mode in-place (no navigation).
	 */
	showSignupTab?: boolean;
	allowAuthenticatedLogin?: boolean;
	onAuthenticated?: () => void | Promise<void>;
};

export default function AuthDialog({
	open,
	onOpenChange,
	returnUrl = AUTH_DIALOG_DEFAULT_RETURN_URL,
	title = "Log in",
	showTitle = false,
	showSignupTab = false,
	allowAuthenticatedLogin = false,
	onAuthenticated,
}: AuthDialogProps) {
	const [mode, setMode] = useState<"login" | "signup" | "emailSignup">("login");
	const { status } = useSession();

	useEffect(() => {
		if (open && status === "authenticated" && !allowAuthenticatedLogin) {
			onOpenChange(false);
			setMode("login");
		}
	}, [allowAuthenticatedLogin, onOpenChange, open, status]);

	const handleOpenChange = (next: boolean) => {
		onOpenChange(next);
		if (!next) {
			setMode("login");
		}
	};

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent
				hideClose
				className={AUTH_DIALOG_CONTENT_CLASSNAME}
				onInteractOutside={(event) => {
					if (mode === "emailSignup") event.preventDefault();
				}}
			>
				<div className="relative mx-auto w-full max-w-md overflow-visible">
					<DialogClose
						className={cn(AUTH_DIALOG_CLOSE_POSITION_CLASSNAME, AUTH_DIALOG_CLOSE_CLASSNAME)}
						aria-label="Close"
					>
						<X className="size-4" />
						<span className="sr-only">Close</span>
					</DialogClose>

					<DialogHeader className={showTitle ? undefined : "sr-only"}>
						<DialogTitle className={showTitle ? "text-left text-lg font-semibold" : undefined}>
							{mode === "login" ? title : "Sign up"}
						</DialogTitle>
						<DialogDescription>
							{mode === "login"
								? "Sign in to continue using Techxeed."
								: "Create an account to continue using Techxeed."}
						</DialogDescription>
					</DialogHeader>

					<div className={AUTH_DIALOG_BODY_CLASSNAME}>
						{showSignupTab ? (
							<>
								{mode !== "emailSignup" ? (
									<div className={AUTH_DIALOG_TAB_LIST_CLASSNAME}>
										<button
											type="button"
											onClick={() => setMode("login")}
											className={authDialogTabTriggerClassName(mode === "login")}
										>
											Log in
										</button>
										<button
											type="button"
											onClick={() => setMode("signup")}
											className={authDialogTabTriggerClassName(mode === "signup")}
										>
											Sign up
										</button>
									</div>
								) : null}
								<div className={cn("flex justify-center", showTitle ? "mt-4" : "")}>
									{mode === "login" ? (
										<LoginCard
											variant="modal"
											returnUrlOverride={returnUrl}
											onSwitchToSignup={() => setMode("signup")}
											onAuthenticated={onAuthenticated}
											allowAuthenticatedLogin={allowAuthenticatedLogin}
										/>
									) : (
										mode === "emailSignup" ? (
											<EmailSignUpCard
												variant="modal"
												returnUrlOverride={returnUrl}
												hideAvatarUpload
												onBack={() => setMode("signup")}
												onVerified={onAuthenticated}
											/>
										) : (
											<SignupEntryCard
												returnUrl={returnUrl}
												onSwitchToLogin={() => setMode("login")}
												onSwitchToEmailSignup={() => setMode("emailSignup")}
												showOneClickSeparator
											/>
										)
									)}
								</div>
							</>
						) : (
							<div className={cn("flex justify-center", showTitle ? "mt-2" : "")}>
								{mode === "login" ? (
									<LoginCard
										variant="modal"
										returnUrlOverride={returnUrl}
										onSwitchToSignup={() => setMode("signup")}
										onAuthenticated={onAuthenticated}
										allowAuthenticatedLogin={allowAuthenticatedLogin}
									/>
								) : (
									mode === "emailSignup" ? (
										<EmailSignUpCard
											variant="modal"
											returnUrlOverride={returnUrl}
											hideAvatarUpload
											onBack={() => setMode("signup")}
											onVerified={onAuthenticated}
										/>
									) : (
										<SignupEntryCard
											returnUrl={returnUrl}
											onSwitchToLogin={() => setMode("login")}
											onSwitchToEmailSignup={() => setMode("emailSignup")}
											showOneClickSeparator
										/>
									)
								)}
							</div>
						)}
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
}
